/*
 * Top level Module for handling Learner
 */

define([
  "marionette",
  "backbone",
  "jquery",
  "configuration/properties",
  "messaging/messenger",
  "modules/learner/learnerView",
  "modules/learner/learnerModel",
  "modules/learner/learnerConfig/learnerConfigModule",
  "modules/learner/learnerResults/learnerResultsModule",
  "modules/accountAdmin/kbAdmin/kbModel",
], function (
  Marionette,
  Backbone,
  $,
  Properties,
  Messenger,
  LearnerView,
  LearnerModel,
  LearnerConfigModule,
  LearnerResultsModule,
  KbModel,
) {
  "use strict";

  const Router = Marionette.AppRouter.extend({
    appRoutes: {
      ":kbId/learner": "_route",
    },
  });

  const WAIT_TIME_IN_MS = 30 * 1000;

  const MESSAGE_KEYS = {
    jobFailedMessageKey: "learner.results.job.failed",
    jobPendingMessageKey: "learner.results.job.pending",
    jobSubmittedMessageKey: "learner.results.job.submitted",
  };

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.clusteringJobDetails = null;
      this.name = "learner";
      this.region = options.region;
      this._setUpVents();
      this.router = new Router({
        controller: this,
      });
      this.updateTimer = null;
    },
    show: function (touchpointId, localeId) {
      const kbId = Messenger.reqres.request("active:kb");
      Backbone.history.navigate(kbId + "/learner");
      document.title = $.t("menuBar.learner") + " - " + Properties.applicationName;
      Messenger.command.execute("change:menu", "learner");
      //"save" learner
      Messenger.onSave(function () {
        console.log("saving learner");
        Messenger.saveAttempted(true); //success
      }, this);

      if (!this.isOpen) {
        const model = new Backbone.Model({
          showResults: true,
        });
        this.model = model;
        this.layout = new LearnerView.learnerLayout({ model: model });
        this.config = this.addSubModule(
          new LearnerConfigModule({
            region: this.layout.learnerConfig,
            touchpointId: touchpointId,
            localeId: localeId,
          }),
        );
        this.results = this.addSubModule(
          new LearnerResultsModule({ region: this.layout.learnerResults }),
        );

        const currentKb = Messenger.reqres.request("active:kb:model");
        const isSemanticKb = currentKb.get("engineMode") === KbModel.ENGINE_MODE.SEMANTIC;
        let latestClusteringJob;
        if (isSemanticKb) {
          latestClusteringJob = new LearnerModel.clusteringJob();
          latestClusteringJob.fetch();
        }

        this.layout.on("show", () => this._showSubViews(latestClusteringJob));

        this.showRegion(this.layout);
      } else {
        //make sure layout region is visible
        this.region.trigger("show");
      }
    },
    _jobPending: function (jobDetails) {
      this.clusteringJobDetails = jobDetails;
      this.layout.setJobStatusMessage(MESSAGE_KEYS.jobPendingMessageKey);
      this._hideResults();
      this._pollServerForJobStatusAndShowClusters(jobDetails.get("jobId"));
    },
    _jobSuccess: function (jobDetails) {
      this.clusteringJobDetails = jobDetails;
      // stop any future polling once we have a terminal status
      if (this.updateTimer) {
        window.clearTimeout(this.updateTimer);
        this.updateTimer = null;
      }
    },
    _jobFailed: function (jobDetails) {
      // since the job failed, allow the user to start a new query
      this.clusteringJobDetails = null;
      const localeDate = Messenger.reqres.request(
        "get:formatted:dateTime:short",
        Date.parse(jobDetails.get("jobScheduledTimestamp")),
      );
      this.layout.setJobStatusMessage(MESSAGE_KEYS.jobFailedMessageKey, {
        scheduledDate: localeDate,
      });

      this._hideResults();

      // stop any future polling once we have a terminal status
      if (this.updateTimer) {
        window.clearTimeout(this.updateTimer);
        this.updateTimer = null;
      }
    },
    _hideResults: function () {
      this.model.set("showResults", false);
      this.layout.render();
    },
    _pollServerForJobStatusAndShowClusters: function (jobId) {
      // clear any existing timer so we don't stack multiple polling loops
      if (this.updateTimer) {
        window.clearTimeout(this.updateTimer);
        this.updateTimer = null;
      }

      const job = new LearnerModel.clusteringJob();
      job.set("jobId", jobId);

      job.fetch({
        error: (model) => {
          // Treat the job as a failure if there was an issue contacting the server.
          this._jobFailed(model);
        },
        success: (model) => {
          if (model.isClusteringJobSuccess()) {
            this._jobSuccess(model);
            this._showSubViewsAndTriggerSearch();
          } else if (model.isClusteringJobFailed()) {
            this._jobFailed(model);
            // reset to default config parameters with search enabled
            this.config.show();
          } else if (model.isClusteringJobPending()) {
            this.layout.setJobStatusMessage(MESSAGE_KEYS.jobPendingMessageKey);
            // keep polling until we get a terminal state
            this.updateTimer = window.setTimeout(() => {
              this._pollServerForJobStatusAndShowClusters(jobId);
            }, WAIT_TIME_IN_MS);
          } else {
            // unknown state: treat the job as a failure to avoid polling forever
            console.error("Unknown clustering job status: " + model.get("status"));
            this._jobFailed(model);
          }
        },
      });
    },
    _showSubViews: function (latestClusteringJob) {
      if (latestClusteringJob) {
        latestClusteringJob.onSyncedPromise().then((jobDetails) => {
          if (jobDetails && jobDetails.get("jobId")) {
            if (jobDetails.isClusteringJobSuccess()) {
              this._jobSuccess(jobDetails);
              // set job details to config parameters and trigger search
              this._showSubViewsAndTriggerSearch();
            } else if (jobDetails.isClusteringJobPending()) {
              this._jobPending(jobDetails);
              this.config.show({
                clusteringJobDetails: jobDetails,
                shouldDisableSearch: true,
              });
            } else if (jobDetails.isClusteringJobFailed()) {
              this._jobFailed(jobDetails);
              // show the config parameters for this job, but reset the jobId so that the user can submit another job.
              jobDetails.set("jobId", "");
              this.config.show({
                clusteringJobDetails: jobDetails,
              });
            }
          } else {
            this.config.show();
          }
        });
      } else {
        this.config.show({
          clusteringJobDetails: this.clusteringJobDetails,
        });
      }
    },
    _showSubViewsAndTriggerSearch: function () {
      // show config parameters and results and disable the search button
      this.model.set("showResults", true);
      this.layout.render();
      this.config.show({
        clusteringJobDetails: this.clusteringJobDetails,
        shouldTriggerSearch: true,
        shouldDisableSearch: true,
      });
    },
    _hideResultsModuleAndShowSubmittedMessage: function (jobDetails) {
      if (this.results) {
        this._hideResults();
        this.config.show({
          clusteringJobDetails: jobDetails,
          shouldDisableSearch: true,
        });
      }
      this.layout.setJobStatusMessage(MESSAGE_KEYS.jobSubmittedMessageKey);
    },
    _route: function (kbId) {
      const isAuthor = Messenger.reqres.request("user:is:author", kbId);
      if (!isAuthor) {
        Backbone.history.navigate("", { trigger: true });
        return;
      }
      Messenger.command.execute("set:active:kb", kbId);
      Messenger.command.execute("change:menu", "learner");
      this.show();
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "clustering:job:submitted",
        (jobDetails) => {
          this.clusteringJobDetails = jobDetails;
          this._hideResultsModuleAndShowSubmittedMessage(jobDetails);
          this._pollServerForJobStatusAndShowClusters(jobDetails.get("jobId"));
        },
        this,
      );

      Messenger.vent.on("show:learner", () => this.show(), this);

      //trigger a search using the given parameters
      Messenger.vent.on(
        "show:learner:and:search",
        (touchpointId, localeId) => {
          //set isOpen to false to make sure we load a fresh version of the learner, so that we get the default config parameters and not some old search
          this.isOpen = false;
          this.show(touchpointId, localeId);
          //trigger the search in the config module as if we were clicking the search button ourselves
          this.config.triggerSearch();
        },
        this,
      );

      Messenger.vent.on(
        "kb:changed",
        function () {
          if (this.layout) {
            this.layout.close();
          }
        },
        this,
      );
    },
  });
});
